/**
 * @file Components/LocalSEO/LocalSEO.jsx
 * @description Local SEO component injecting JSON-LD structured data
 * for local business schema markup to improve search engine visibility.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

const LocalSEO = ({
  businessName = 'Nikaba Travels',
  address,
  city,
  state,
  postalCode,
  country = 'India',
  phone = '+91-8002406800',
  email = 'info@nikabatravels.com',
  website = 'https://nikabatravels.com',
  description = 'Best Inbound and Outbound Tour Operator in India. Offering customized tour packages, visa assistance, hotel bookings, and complete travel solutions.',
  image = '/assets/Asset 2Nikaba travels - Ci-1.png',
  priceRange = '₹₹₹',
  openingHours = [
    'Mo-Fr 09:00-18:00',
    'Sa 09:00-18:00'
  ],
  socialProfiles = [
    'https://facebook.com/nikabatravels',
    'https://twitter.com/nikabatravels',
    'https://instagram.com/nikabatravels'
  ],
  services = [
    'Tour Packages',
    'Flight Booking',
    'Hotel Reservations',
    'Car Rental',
    'Travel Insurance',
    'Visa Assistance'
  ]
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: businessName,
    description: description,
    image: image,
    url: website,
    telephone: phone,
    email: email,
    priceRange: priceRange,
    address: address ? {
      '@type': 'PostalAddress',
      streetAddress: address,
      addressLocality: city,
      addressRegion: state,
      postalCode: postalCode,
      addressCountry: country
    } : undefined,
    openingHoursSpecification: openingHours.map(hours => {
      const [days, time] = hours.split(' ');
      const [openTime, closeTime] = time.split('-');
      
      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: days.split('-').map(day => {
          const dayMap = {
            'Mo': 'Monday',
            'Tu': 'Tuesday',
            'We': 'Wednesday',
            'Th': 'Thursday',
            'Fr': 'Friday',
            'Sa': 'Saturday',
            'Su': 'Sunday'
          };
          return dayMap[day] || day;
        }),
        opens: openTime,
        closes: closeTime
      };
    }),
    sameAs: socialProfiles,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Travel Services',
      itemListElement: services.map(service => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service
        }
      }))
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '150'
    }
  };

  // Remove undefined properties
  Object.keys(structuredData).forEach(key => {
    if (structuredData[key] === undefined) {
      delete structuredData[key];
    }
  });

  return (
    <Helmet>
      {/* Local Business Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Local Business Meta Tags */}
      <meta name="geo.region" content={state ? `${country}-${state}` : country} />
      <meta name="geo.placename" content={city} />
      {postalCode && <meta name="geo.position" content={postalCode} />}
      <meta name="ICBM" content="" />

      {/* Business Contact Info */}
      {phone && <meta name="telephone" content={phone} />}
      <meta name="email" content={email} />

      {/* Local Keywords */}
      <meta name="keywords" content={`travel agency ${city}, tours ${city}, ${businessName}, travel services ${state}, vacation packages ${city}`} />
    </Helmet>
  );
};

LocalSEO.propTypes = {
  businessName: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  country: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  website: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  priceRange: PropTypes.string,
  openingHours: PropTypes.arrayOf(PropTypes.string),
  socialProfiles: PropTypes.arrayOf(PropTypes.string),
  services: PropTypes.arrayOf(PropTypes.string)
};

export default LocalSEO;
